import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faChevronLeft, faChevronRight, faTrashAlt, faLightbulb, faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { getAssetsByGateway } from "src/core/api/services/command-module/zone.service";
import type { Zone, ZoneAsset } from "../../components/types";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";

type AssetPickerProps = {
  gatewayCode: string;
  assignedAssets: ZoneAsset[];
  onAssign: (assets: ZoneAsset[]) => void;
  zone?: Zone | null;
  isEditMode?: boolean;
  isLoading?: boolean;
};

type SelectionKey = string;
type AvailableZoneAsset = ZoneAsset & {
  blockCode?: string;
  blockToFloorCode?: string;
};

export function AssetPicker({
  gatewayCode,
  assignedAssets,
  onAssign,
  zone,
  isEditMode = false,
  isLoading: isLoadingProps,
}: AssetPickerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedAvailableKeys, setSelectedAvailableKeys] = useState<Set<SelectionKey>>(new Set());
  const [selectedAssignedKeys, setSelectedAssignedKeys] = useState<Set<SelectionKey>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchText]);

  const { data: availableData, isLoading: isLoadingAvailable } = useQuery({
    queryKey: ["assetsByGateway", gatewayCode, debouncedSearchText],
    queryFn: ({ signal }) => getAssetsByGateway(gatewayCode, debouncedSearchText, signal),
    enabled: !!gatewayCode,
  });

  const isLoading = isLoadingProps || isLoadingAvailable;

  const availableAssets: AvailableZoneAsset[] = useMemo(() => {
    const raw = availableData?.data || availableData || [];
    return raw.map((item: any, idx: number) => {
      const assetCode = item.assetCode || "";
      const pickKey: SelectionKey = assetCode || String(item.assetId || item.id || idx);
      return {
        assetId: item.assetId || item.id,
        assetCode,
        assetName: item.assetName || item.name || item.sensorDisplayName || `Asset ${pickKey}`,
        assetType: item.assetType || item.type || "Unknown",
        blockName: item.blockName,
        blockCode: item.blockCode,
        blockToFloorCode: item.blockToFloorCode,
        street: item.street,
        _pickKey: pickKey,
      };
    });
  }, [availableData]);

  const assignedKeys = useMemo(
    () => new Set(assignedAssets.map((a) => a.assetCode || String(a.assetId || "")).filter((k): k is SelectionKey => !!k)),
    [assignedAssets],
  );

  const filteredAvailable = useMemo(() => {
    if (!searchText.trim()) return availableAssets;
    const lower = searchText.toLowerCase();
    return availableAssets.filter(
      (a) =>
        (a.assetName || "").toLowerCase().includes(lower) ||
        (a.assetCode || "").toLowerCase().includes(lower) ||
        (a.assetType || "").toLowerCase().includes(lower),
    );
  }, [availableAssets, searchText]);

  const selectableFilteredAvailable = useMemo(
    () => filteredAvailable.filter((a) => !assignedKeys.has((a as any)._pickKey)),
    [filteredAvailable, assignedKeys],
  );

  const isAllSelected =
    selectableFilteredAvailable.length > 0 && selectedAvailableKeys.size === selectableFilteredAvailable.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedAvailableKeys(new Set());
    } else {
      const keys = selectableFilteredAvailable.map((a) => (a as any)._pickKey);
      setSelectedAvailableKeys(new Set(keys));
    }
  };

  const toggleSelect = (asset: ZoneAsset) => {
    const key: SelectionKey = (asset as any)._pickKey;
    if (!key) return;
    setSelectedAvailableKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const moveSelectedToAssigned = () => {
    const toAssign = availableAssets.filter((a) => {
      const key = (a as any)._pickKey;
      return key && selectedAvailableKeys.has(key);
    });
    const combined = [...assignedAssets, ...toAssign];
    onAssign(combined);
    setSelectedAvailableKeys(new Set());
  };

  const hasSelectionForRemoval = selectedAssignedKeys.size > 0;

  const onRemoveSelected = () => {
    if (!hasSelectionForRemoval) return;
    const toRemove = new Set(selectedAssignedKeys);
    onAssign(isEditMode
      ? assignedAssets.map((asset) => {
          const key = asset.assetCode || String(asset.assetId || "");
          return toRemove.has(key) ? { ...asset, isRemove: true } : asset;
        })
      : assignedAssets.filter((asset) => {
          const key = asset.assetCode || String(asset.assetId || "");
          return !toRemove.has(key);
        }));
    setSelectedAssignedKeys(new Set());
  };

  const toggleAssignedSelect = (asset: ZoneAsset) => {
    const key = asset.assetCode || String(asset.assetId || "");
    if (!key) return;
    setSelectedAssignedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleRemoveAsset = (asset: ZoneAsset) => {
    onAssign(
      assignedAssets.map((assignedAsset) =>
        assignedAsset.assetCode === asset.assetCode
          ? { ...assignedAsset, isRemove: !assignedAsset.isRemove }
          : assignedAsset,
      ),
    );
  };

  const handleViewAssetFloorPlan = (asset: AvailableZoneAsset) => {
    if (!gatewayCode || !asset.blockCode || !asset.blockToFloorCode) return;

    navigate(
      `/asset/${btoa(gatewayCode)}/configuration/zones/floorplan/${btoa(asset.blockCode)}/${encodeURIComponent(asset.blockToFloorCode)}?gatewayCode=${encodeURIComponent(gatewayCode)}`,
      {
        state: {
          gatewayCode,
          zone,
          assignedAssets,
          returnPath: location.pathname,
        },
      },
    );
  };

  useEffect(() => {
    setSelectedAvailableKeys(new Set());
    setSelectedAssignedKeys(new Set());
    setSearchText("");
    setDebouncedSearchText("");
  }, [gatewayCode]);

  return (
    <div className="asset-picker">
      <div className="asset-picker__panel asset-picker__panel--assigned">
        <div className="asset-picker__panel-header">
          <div className="asset-picker__panel-title">
            ASSIGNED ASSETS
            <span className="asset-picker__panel-badge asset-picker__panel-badge--assigned">
              <span className="asset-picker__panel-badge-dot asset-picker__panel-badge-dot--assigned" />
              {assignedAssets.length} assets
            </span>
          </div>
          {!isEditMode && (
            <button
              type="button"
              className="asset-picker__clear"
              onClick={() => onAssign([])}
              disabled={assignedAssets.length === 0}
            >
              <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: "0.7rem" }} />
              Clear All
            </button>
          )}
        </div>
        <div className="asset-picker__list">
          <SkeletonComponent
            wrapperHeight="310px"
            skeletonHeight="51.66px"
            count={6}
            isLoading={!!isLoadingProps}
          >
            {assignedAssets.length === 0 ? (
              <div className="asset-picker__empty">
                <div className="asset-picker__empty-icon">📋</div>
                <div>No assets assigned yet. Select items from the list on the right to add them to this zone.</div>
              </div>
            ) : (
              assignedAssets.map((asset) => {
                const key = asset.assetCode || String(asset.assetId || "");
                const isChecked = !!key && selectedAssignedKeys.has(key);
                return (
                  <div key={key} className="zone-card asset-picker__assigned-card">
                    <div className="zone-card__info">
                      {!isEditMode && (
                        <input
                          type="checkbox"
                          className="asset-picker__item-checkbox"
                          checked={isChecked}
                          onChange={() => toggleAssignedSelect(asset)}
                          disabled={!!asset.isRemove}
                        />
                      )}
                      <div>
                        <div className={`zone-card__title ${asset.isRemove ? "asset-picker__assigned-name--removed" : ""}`}>
                          {asset.assetName}
                        </div>
                      </div>
                    </div>
                    <div className="zone-card__actions">
                      <button
                        type="button"
                        className="zone-card__action"
                        title="Light Node"
                        disabled={asset.isRemove}
                      >
                        <FontAwesomeIcon icon={faLightbulb} />
                      </button>
                      {isEditMode && (
                        <button
                          type="button"
                          className="zone-card__action zone-card__action--danger"
                          title={asset.isRemove ? "Keep asset" : "Remove"}
                          onClick={() => handleRemoveAsset(asset)}
                        >
                        <FontAwesomeIcon icon={asset.isRemove ? faCheck : faXmark} />
                      </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </SkeletonComponent>
        </div>
      </div>

      <div className="asset-picker__actions">
        {!isEditMode && (
          <button
            type="button"
            className={`asset-picker__action-btn ${hasSelectionForRemoval ? "asset-picker__action-btn--primary" : ""}`}
            onClick={onRemoveSelected}
            disabled={!hasSelectionForRemoval}
            title="Remove selected"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
        <button
          type="button"
          className={`asset-picker__action-btn ${selectedAvailableKeys.size > 0 ? "asset-picker__action-btn--primary" : ""}`}
          onClick={moveSelectedToAssigned}
          disabled={selectedAvailableKeys.size === 0}
          title="Add selected"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      </div>

      <div className="asset-picker__panel asset-picker__panel--available">
        <div className="asset-picker__panel-header">
          <div className="asset-picker__panel-title">AVAILABLE ASSETS</div>
          <span className="asset-picker__panel-badge asset-picker__panel-badge--available">
            <span className="asset-picker__panel-badge-dot asset-picker__panel-badge-dot--available" />
            {selectedAvailableKeys.size} selected
          </span>
        </div>
        <div className="asset-picker__search">
          <input
            type="text"
            className="asset-picker__search-input"
            placeholder="Search assets..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div
          className="asset-picker__select-all"
          onClick={toggleSelectAll}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            onClick={(e) => e.stopPropagation()}
            className="asset-picker__item-checkbox"
          />
          <span>SELECT ALL</span>
        </div>
          <SkeletonComponent
            wrapperHeight="210px"
            skeletonHeight="42px"
            count={5}
            isLoading={isLoadingAvailable}
          >
            <div className="asset-picker__list">
              {isLoading && (
                <div style={{ padding: "1rem", textAlign: "center", color: "#64748b", fontSize: "0.85rem" }}>
                  Loading assets...
                </div>
              )}
              {!isLoading && filteredAvailable.length === 0 && (
                <div className="asset-picker__empty">
                  <div>No assets available.</div>
                </div>
              )}
              {filteredAvailable.map((asset) => {
                const key = (asset as any)._pickKey;
                const isChecked = !!key && selectedAvailableKeys.has(key);
                const isAssigned = !!key && assignedKeys.has(key);
                return (
                  <div
                    key={key}
                    className={`asset-picker__item ${isChecked ? "asset-picker__item--selected" : ""} ${isAssigned ? "asset-picker__item--disabled" : ""}`}
                    style={{ opacity: isAssigned ? 0.5 : 1, pointerEvents: isAssigned ? "none" : "auto" }}
                  >
                    <input
                      type="checkbox"
                      className="asset-picker__item-checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelect(asset)}
                      disabled={isAssigned || !key}
                    />
                    <span className="asset-picker__item-name">{asset.assetName}</span>
                    <button
                      type="button"
                      className="asset-picker__item-action"
                      title={asset.blockCode && asset.blockToFloorCode ? "View floor plan" : "Floor plan unavailable"}
                      aria-label={`View floor plan for ${asset.assetName}`}
                      disabled={!gatewayCode || !asset.blockCode || !asset.blockToFloorCode}
                      onClick={() => handleViewAssetFloorPlan(asset)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </div>
                );
              })}
            </div>
          </SkeletonComponent>
      </div>
    </div>
  );
}
